import purchaseOrderService from "../../../../services/procurementService/purchaseOrderService";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CACHE_KEY_PURCHASEORDERS } from "../../../../utils/constants";
import { PurchaseOrderInterface } from "../../../../models/ProcurementModule/PurchateOrder/PurchaseOrder";
import ToastMessage from "../../../../common/ToastMessage";

interface UpdatePurchaseOrderContext {
  previousPurchaseOrders: PurchaseOrderInterface[];
}

const useUpdatePurchaseOrder = (
  page: number,
  pageSize: number,
  fileNames: {
    fileUrl: string;
    fileName: string;
  }[]
) => {
  const queryClient = useQueryClient();

  return useMutation<
    PurchaseOrderInterface,
    Error,
    PurchaseOrderInterface,
    UpdatePurchaseOrderContext
  >({
    mutationFn: async (purchaseOrder: PurchaseOrderInterface) => {
      // Assuming the ID is stored in the purchaseOrder object or can be derived
      const { id, ...purchaseOrderData } = purchaseOrder;
      return purchaseOrderService.Update(id, purchaseOrder, fileNames);
    },

    onMutate: async (updatedPurchaseOrder: PurchaseOrderInterface) => {
      await queryClient.cancelQueries({
        queryKey: [CACHE_KEY_PURCHASEORDERS, page, pageSize],
      });

      const previousPurchaseOrders =
        queryClient.getQueryData<PurchaseOrderInterface[]>([
          CACHE_KEY_PURCHASEORDERS,
          page,
          pageSize,
        ]) || [];

      queryClient.setQueryData<PurchaseOrderInterface[]>(
        [CACHE_KEY_PURCHASEORDERS, page, pageSize],
        (purchaseOrders = []) =>
          purchaseOrders.map((purchaseOrder) =>
            purchaseOrder.id === updatedPurchaseOrder.id
              ? updatedPurchaseOrder
              : purchaseOrder
          )
      );

      return { previousPurchaseOrders };
    },

    onSuccess: (savedPurchaseOrder) => {
      queryClient.setQueryData<PurchaseOrderInterface[]>(
        [CACHE_KEY_PURCHASEORDERS, page, pageSize],
        (purchaseOrders) =>
          purchaseOrders?.map((purchaseOrder) =>
            purchaseOrder.id === savedPurchaseOrder.id
              ? savedPurchaseOrder
              : purchaseOrder
          )
      );
    },

    onError: (error, updatedPurchaseOrder, context) => {
      ToastMessage.Failed();
      if (!context) return;

      queryClient.setQueryData<PurchaseOrderInterface[]>(
        [CACHE_KEY_PURCHASEORDERS, page, pageSize],
        context.previousPurchaseOrders
      );
    },
  });
};

export default useUpdatePurchaseOrder;
